import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { COMPANY_INFO } from '../config/assets';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to submit');

      toast.success('Enquiry submitted successfully! We\'ll get back to you soon.');
      reset();
    } catch (error) {
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  return (
    <div className="min-h-screen pt-16" data-testid="contact-page">
      {/* Hero */}
      <section className="section-spacing bg-[radial-gradient(1200px_600px_at_50%_50%,#EEEDEC_0%,#F7F7F6_45%,#FAFAF9_100%)]">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Get in Touch</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ready to transform your space? Contact us for a free consultation and quote
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <motion.div className="lg:col-span-2" {...fadeInUp}>
              <Card>
                <CardHeader>
                  <CardTitle>Send us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          {...register('name', { required: 'Name is required' })}
                          placeholder="John Smith"
                          className="mt-1.5"
                          data-testid="contact-form-name-input"
                        />
                        {errors.name && (
                          <p className="text-destructive text-sm mt-1" data-testid="error-message">
                            {errors.name.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address'
                            }
                          })}
                          placeholder="john@example.com"
                          className="mt-1.5"
                          data-testid="contact-form-email-input"
                        />
                        {errors.email && (
                          <p className="text-destructive text-sm mt-1" data-testid="error-message">
                            {errors.email.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          {...register('phone')}
                          placeholder="+614 XX XXX XXX"
                          className="mt-1.5"
                          data-testid="contact-form-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="project_type">Project Type</Label>
                        <select
                          id="project_type"
                          {...register('project_type')}
                          className="w-full mt-1.5 h-10 px-3 rounded-md border border-input bg-background text-sm"
                          data-testid="contact-form-project-type-select"
                        >
                          <option value="">Select...</option>
                          <option value="commercial">Commercial</option>
                          <option value="residential">Residential</option>
                          <option value="both">Both</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        rows="6"
                        {...register('message', { required: 'Message is required' })}
                        placeholder="Tell us about your project..."
                        className="mt-1.5"
                        data-testid="contact-form-message-textarea"
                      />
                      {errors.message && (
                        <p className="text-destructive text-sm mt-1" data-testid="error-message">
                          {errors.message.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="btn-primary w-full md:w-auto"
                      disabled={isSubmitting}
                      data-testid="contact-form-submit-button"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div className="lg:col-span-1 space-y-6" {...fadeInUp} transition={{ delay: 0.1 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Email</p>
                      <a
                        href={`mailto:${COMPANY_INFO.email}`}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {COMPANY_INFO.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Phone</p>
                      <p className="text-sm text-muted-foreground">{COMPANY_INFO.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Location</p>
                      <p className="text-sm text-muted-foreground">{COMPANY_INFO.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Business Hours</p>
                      <p className="text-sm text-muted-foreground">Mon - Sat: 8:00 AM - 6:00 PM</p>
                      <p className="text-sm text-muted-foreground">Sun: Closed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-2">Why Choose Us?</h3>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>• 10+ years of experience</li>
                    <li>• Professional equipment & techniques</li>
                    <li>• Competitive pricing</li>
                    <li>• Quality guarantee</li>
                    <li>• Timely project completion</li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
